import { useSuspenseQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle, ExternalLink, XCircle } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import { AlertBar } from "@/components/AlertBar";
import { Badge } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import { orpc } from "@/orpc/client";
import { formatCurrency } from "@/util/format";

export function PayoutRiskAlert({ days }: { days: number }) {
	const { data } = useSuspenseQuery(
		orpc.dashboard.payoutHealth.queryOptions({
			input: { days },
		}),
	);

	if (!data.hasRisk) return null;

	return (
		<AlertBar variant="error" title="Payout Risk Detected" className="mb-6">
			One or more apps have payout issues that need your attention. Review the
			details below and take action.
		</AlertBar>
	);
}

export function PayoutHealthCard({ days }: { days: number }) {
	const { data } = useSuspenseQuery(
		orpc.dashboard.payoutHealth.queryOptions({
			input: { days },
		}),
	);

	if (data.items.length === 0) {
		return null;
	}

	return (
		<div className="space-y-4">
			<div className="rounded-xl border border-base-content/10 bg-base-100 p-6">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-semibold text-base-content">
						Payout Health
					</h3>
					<div className="text-sm text-base-content/60">
						Balance vs Estimated Payouts
					</div>
				</div>

				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead className="border-b border-base-content/10 text-base-content/60">
							<tr>
								<th className="pb-3 pr-4 font-medium">App</th>
								<th className="pb-3 pr-4 font-medium">Stripe Account</th>
								<th className="pb-3 pr-4 font-medium">Est. Payouts</th>
								<th className="pb-3 pr-4 font-medium">Stripe Balance</th>
								<th className="pb-3 text-right font-medium">Status</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-base-content/10">
							{data.items.map((item) => (
								<tr key={item.appId} className="group">
									<td className="py-3 pr-4 font-medium text-base-content">
										{item.appName}
									</td>
									<td className="py-3 pr-4 text-base-content/80">
										{item.stripeAccountLabel || (
											<span className="italic text-base-content/40">None</span>
										)}
									</td>
									<td className="py-3 pr-4 text-base-content/80">
										<div>
											{formatCurrency(item.estimatedPayouts, item.currency)}
										</div>
										{item.failedPayouts > 0 && (
											<div className="text-xs text-error">
												{formatCurrency(item.failedPayouts, item.currency)}{" "}
												failed
											</div>
										)}
									</td>
									<td className="py-3 pr-4 text-base-content/80">
										{item.stripeBalance !== null ? (
											formatCurrency(item.stripeBalance, item.currency)
										) : (
											<span className="text-base-content/40">
												Not Connected
											</span>
										)}
									</td>
									<td className="py-3 text-right">
										<div className="flex justify-end gap-2">
											{item.status === "healthy" && (
												<Badge color="green">
													<CheckCircle className="size-3" />
													Healthy
												</Badge>
											)}
											{item.status === "warning" && (
												<Badge color="yellow">
													<AlertCircle className="size-3" />
													Action Needed
												</Badge>
											)}
											{item.status === "critical" && (
												<Badge color="red">
													<XCircle className="size-3" />
													Risk
												</Badge>
											)}
											{item.status === "unknown" && (
												<Badge color="zinc">Unknown</Badge>
											)}

											{item.stripeAccountId &&
												(item.status === "warning" ||
													item.status === "critical") && (
													<Button
														outline
														className="h-6 min-h-0 px-2 text-xs"
														href={`https://dashboard.stripe.com/${item.stripeAccountId}/balance`}
														target="_blank"
													>
														Top up <ExternalLink className="ml-1 size-3" />
													</Button>
												)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

export function PayoutHealthSkeleton() {
	return (
		<div className="rounded-xl border border-base-content/10 bg-base-100 p-6">
			<div className="mb-4 flex items-center justify-between">
				<Skeleton width={120} height={24} />
				<Skeleton width={150} height={16} />
			</div>
			<div className="space-y-4">
				{[1, 2].map((i) => (
					<div key={i} className="flex justify-between">
						<Skeleton width={100} />
						<Skeleton width={80} />
						<Skeleton width={80} />
						<Skeleton width={80} />
						<Skeleton width={80} />
						<Skeleton width={80} />
					</div>
				))}
			</div>
		</div>
	);
}
